import React from 'react'
import { useTranslation } from 'react-i18next'
import { Typography } from '@mui/material'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Dispatch } from '../store'
import { ListItemLocation } from '../components/ListItemLocation'
import { NetworkTagEditor } from '../components/NetworkTagEditor'
import { ListHorizontal } from '../components/ListHorizontal'
import { ConfirmIconButton } from '../buttons/ConfirmIconButton'
import { ShareButton } from '../buttons/ShareButton'
import { DeleteButton } from '../buttons/DeleteButton'
import { Container } from '../components/Container'
import { UsersTab } from '../components/UsersTab'
import { Notice } from '../components/Notice'
import { Title } from '../components/Title'

export const NetworkHeaderMenu: React.FC<{
  network: INetwork
  email: string
  children?: React.ReactNode | React.ReactNode[]
}> = ({ network, email, children }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch<Dispatch>()

  return (
    <Container
      gutterBottom
      bodyProps={{ verticalOverflow: true }}
      header={
        <>
          <Typography variant="h1">
            <Title>{network.name || t('networkHeaderMenu.unknownNetwork', 'Unknown Network')}</Title>
            {network.shared ? (
              <ConfirmIconButton
                confirm
                title={t('networkHeaderMenu.leaveNetwork', 'Leave network')}
                icon="arrow-right-from-bracket"
                size="md"
                confirmProps={{
                  children: (
                    <>
                      <Notice severity="error" fullWidth gutterBottom>
                        {t('networkHeaderMenu.leavingNoticePrefix', 'You will be leaving')} <i>{network.name}. </i>
                      </Notice>
                      {t(
                        'networkHeaderMenu.leavingBody',
                        'The network will have to be re-shared to you if you wish to access it again.'
                      )}
                    </>
                  ),
                }}
                onClick={async () => {
                  await dispatch.networks.unshareNetwork({ networkId: network.id, email })
                  history.push('/networks')
                }}
              />
            ) : (
              network.permissions.includes('MANAGE') && (
                <>
                  <ShareButton to={`/networks/${network.id}/share`} title={t('networkHeaderMenu.shareAccess', 'Share access')} />
                  <DeleteButton
                    title={t('networkHeaderMenu.deleteNetwork', 'Delete Network')}
                    warning={
                      <>
                        <Notice severity="error" fullWidth gutterBottom>
                          {t('networkHeaderMenu.deletingNoticePrefix', 'You will be permanently deleting')} <i>{network.name}. </i>
                        </Notice>
                        {t(
                          'networkHeaderMenu.deletingBody',
                          "This will remove this network for you and all of the network's users."
                        )}
                      </>
                    }
                    onDelete={async () => {
                      await dispatch.networks.deleteNetwork(network)
                      history.push('/networks')
                    }}
                  />
                </>
              )
            )}
          </Typography>
          <NetworkTagEditor network={network} />
          <ListHorizontal dense>
            <ListItemLocation
              title={t('networkHeaderMenu.details', 'Details')}
              icon="circle-info"
              iconColor="grayDarker"
              to={`/networks/${network.id}`}
              exactMatch
              dense
            />
            <UsersTab instance={network} to={`/networks/${network.id}/users`} />
          </ListHorizontal>
        </>
      }
    >
      {children}
    </Container>
  )
}
